// src/components/IssueCard.js
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../constants/routes';
import { formatScore } from '../utils';
import { ScoreTooltip } from './ScoreTooltips';
import { useStaffContext } from '../contexts/StaffContext';

const IssueCard = ({ 
    issue, 
    clientId, 
    onClick,
    editable = false,
    statusOptions = [],
    severityOptions = [],
    onStatusChange,
    onSeverityChange,
    'data-testid': dataTestId
}) => {
    const navigate = useNavigate();
    const { getDriDisplayName } = useStaffContext();
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [showSeverityDropdown, setShowSeverityDropdown] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const statusRef = useRef(null);
    const severityRef = useRef(null);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (statusRef.current && !statusRef.current.contains(event.target)) {
                setShowStatusDropdown(false);
            }
            if (severityRef.current && !severityRef.current.contains(event.target)) {
                setShowSeverityDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    
    const handleClick = (e) => {
        if (e.target.closest('.editable-control')) return;
        
        if (onClick) {
            onClick(issue);
        } else if ((issue.issueId || issue.id) && clientId) {
            navigate(`${ROUTES.ACCOUNTS}/${clientId}/issues/${issue.issueId || issue.id}`);
        }
    };

    const handleStatusChange = async (newStatus) => {
        if (onStatusChange && newStatus !== issue.status) {
            setIsUpdating(true);
            try {
                await onStatusChange(issue.issueId || issue.id, newStatus);
            } finally {
                setIsUpdating(false);
                setShowStatusDropdown(false);
            }
        } else {
            setShowStatusDropdown(false);
        }
    };

    const handleSeverityChange = async (newSeverity) => {
        if (onSeverityChange && newSeverity !== issue.severity) {
            setIsUpdating(true);
            try {
                await onSeverityChange(issue.issueId || issue.id, newSeverity);
            } finally {
                setIsUpdating(false);
                setShowSeverityDropdown(false);
            }
        } else {
            setShowSeverityDropdown(false);
        }
    };

    // Get status badge color
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'open':
                return { bg: 'rgba(0, 156, 220, 0.1)', color: 'var(--pallium-info-cyan)' };
            case 'in progress':
            case 'in-progress':
                return { bg: 'rgba(110, 79, 196, 0.1)', color: 'var(--pallium-primary-purple)' };
            case 'on hold':
            case 'on-hold':
                return { bg: 'rgba(239, 163, 29, 0.1)', color: 'var(--pallium-warning-yellow)' };
            case 'resolved':
            case 'closed':
                return { bg: 'rgba(19, 185, 85, 0.1)', color: 'var(--pallium-success-green)' };
            default:
                return { bg: 'var(--pallium-stroke-lighter)', color: 'var(--pallium-mute-text)' };
        }
    };

    // Get severity color
    const getSeverityStyle = (severity) => {
        switch (severity?.toLowerCase()) {
            case 'high':
            case 'critical':
                return { bg: 'rgba(252, 57, 57, 0.1)', color: 'var(--pallium-error-red)' };
            case 'medium':
                return { bg: 'rgba(239, 163, 29, 0.1)', color: 'var(--pallium-warning-yellow)' };
            case 'low':
                return { bg: 'rgba(19, 185, 85, 0.1)', color: 'var(--pallium-success-green)' };
            default:
                return { bg: 'var(--pallium-stroke-lighter)', color: 'var(--pallium-mute-text)' };
        }
    };

    // Truncate description to a snippet
    const truncateText = (text, maxLength = 150) => {
        if (!text) return null;
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength).trim() + '...';
    };

    // Get the name from various possible fields
    const issueName = issue.issueName || issue.name || 'Untitled Issue';
    const status = issue.status;
    const statusStyle = getStatusColor(status);
    
    // Get severity
    const severity = issue.severity;
    const severityStyle = getSeverityStyle(severity);
    
    // Get DRI from various possible fields
    const dri = issue.assigned_to || issue.dri || issue.primary_staff || null;
    
    // Get department
    const department = issue.department || null;
    
    // Get description
    const description = issue.description || null;
    
    // Get blocked initiatives count
    const blockedInitiatives = issue.blockedInitiatives ?? issue.blocked_initiative_count ?? null;
    
    // Get risk score
    const riskScore = issue.riskScore ?? issue.score ?? null;

    return (
        <div
            onClick={handleClick}
            className="p-4 rounded-lg transition-all duration-200 cursor-pointer"
            data-testid={dataTestId}
            style={{
                backgroundColor: 'var(--pallium-white)',
                border: '1px solid var(--pallium-stroke-light)',
                boxShadow: 'var(--pallium-shadow-sm)',
                opacity: isUpdating ? 0.7 : 1
            }}
            onMouseOver={(e) => {
                e.currentTarget.style.boxShadow = 'var(--pallium-shadow-md)';
                e.currentTarget.style.borderColor = 'var(--pallium-error-red)';
            }}
            onMouseOut={(e) => {
                e.currentTarget.style.boxShadow = 'var(--pallium-shadow-sm)';
                e.currentTarget.style.borderColor = 'var(--pallium-stroke-light)';
            }}
        >
            {/* Header: Icon + Issue Name + Status */}
            <div className="flex items-start gap-3 mb-2">
                {/* Warning/Alert Icon */}
                <div 
                    className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(252, 57, 57, 0.1)', color: 'var(--pallium-error-red)' }}
                >
                    <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                </div>
                <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm leading-tight" style={{ color: 'var(--pallium-text-primary)' }}>
                        {issueName}
                    </h4>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {/* Status - Editable or Static */}
                        {editable && statusOptions.length > 0 ? (
                            <div className="relative editable-control" ref={statusRef}>
                                <button
                                    onClick={(e) => { e.stopPropagation(); setShowStatusDropdown(!showStatusDropdown); }}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-all"
                                    style={{ 
                                        backgroundColor: statusStyle.bg, 
                                        color: statusStyle.color,
                                        border: '1px dashed currentColor'
                                    }}
                                    title="Click to change status"
                                >
                                    {status || 'Set Status'}
                                    <svg className="w-3 h-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>
                                {showStatusDropdown && (
                                    <div 
                                        className="absolute left-0 top-full mt-1 z-50 rounded-lg shadow-lg py-1 min-w-[120px]"
                                        style={{ backgroundColor: 'var(--pallium-white)', border: '1px solid var(--pallium-stroke-light)' }}
                                    >
                                        {statusOptions.map(opt => (
                                            <button
                                                key={opt}
                                                onClick={(e) => { e.stopPropagation(); handleStatusChange(opt); }}
                                                className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 transition-colors"
                                                style={{ 
                                                    color: opt === status ? 'var(--pallium-primary-purple)' : 'var(--pallium-text-primary)',
                                                    fontWeight: opt === status ? 600 : 400
                                                }}
                                            >
                                                {opt}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : status && (
                            <span 
                                className="px-2 py-0.5 rounded text-xs font-medium"
                                style={{ backgroundColor: statusStyle.bg, color: statusStyle.color }}
                            >
                                {status}
                            </span>
                        )}
                        {/* Severity - Editable or Static */}
                        {editable && severityOptions.length > 0 ? (
                            <div className="relative editable-control" ref={severityRef}>
                                <button
                                    onClick={(e) => { e.stopPropagation(); setShowSeverityDropdown(!showSeverityDropdown); }}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-all"
                                    style={{ 
                                        backgroundColor: severityStyle.bg, 
                                        color: severityStyle.color,
                                        border: '1px dashed currentColor'
                                    }}
                                    title="Click to change severity"
                                >
                                    {severity ? severity.charAt(0).toUpperCase() + severity.slice(1) : 'Set Severity'}
                                    <svg className="w-3 h-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>
                                {showSeverityDropdown && (
                                    <div 
                                        className="absolute left-0 top-full mt-1 z-50 rounded-lg shadow-lg py-1 min-w-[100px]"
                                        style={{ backgroundColor: 'var(--pallium-white)', border: '1px solid var(--pallium-stroke-light)' }}
                                    >
                                        {severityOptions.map(opt => (
                                            <button
                                                key={opt}
                                                onClick={(e) => { e.stopPropagation(); handleSeverityChange(opt); }}
                                                className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 transition-colors"
                                                style={{ 
                                                    color: opt.toLowerCase() === severity?.toLowerCase() ? 'var(--pallium-primary-purple)' : 'var(--pallium-text-primary)',
                                                    fontWeight: opt.toLowerCase() === severity?.toLowerCase() ? 600 : 400
                                                }}
                                            >
                                                {opt.charAt(0).toUpperCase() + opt.slice(1)}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : severity && (
                            <span 
                                className="px-2 py-0.5 rounded text-xs font-medium"
                                style={{ backgroundColor: severityStyle.bg, color: severityStyle.color }}
                            >
                                {severity.charAt(0).toUpperCase() + severity.slice(1)}
                            </span>
                        )}
                    </div>
                </div>
                {/* Risk Score */}
                {riskScore !== null && (
                    <div className="text-right flex-shrink-0">
                        <div className="text-xs flex items-center justify-end gap-0.5" style={{ color: 'var(--pallium-text-tertiary)' }}>
                            Risk
                            <ScoreTooltip type="issueRisk" size="sm" />
                        </div>
                        <div className="text-sm font-bold" style={{ color: 'var(--pallium-error-red)' }}>
                            {formatScore ? formatScore(riskScore) : (typeof riskScore === 'number' ? riskScore.toFixed(2) : riskScore)}
                        </div>
                    </div>
                )}
            </div>

            {/* Description snippet */}
            {description && (
                <p className="text-xs mb-2 leading-relaxed" style={{ color: 'var(--pallium-text-secondary)' }}>
                    {truncateText(description)}
                </p>
            )}

            {/* Department and DRI */}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs mb-2" style={{ color: 'var(--pallium-text-secondary)' }}>
                {department && (
                    <div>
                        <span className="font-medium" style={{ color: 'var(--pallium-text-tertiary)' }}>Dept:</span>
                        <span className="ml-1">{department}</span>
                    </div>
                )}
                {dri && (
                    <div>
                        <span className="font-medium" style={{ color: 'var(--pallium-text-tertiary)' }}>DRI:</span>
                        <span className="ml-1">{getDriDisplayName(dri)}</span>
                    </div>
                )}
            </div>

            {/* Footer: Blocked Initiatives */}
            {blockedInitiatives !== null && blockedInitiatives > 0 && (
                <div className="text-xs pt-2" style={{ borderTop: '1px solid var(--pallium-stroke-lighter)', color: 'var(--pallium-warning-yellow)' }}>
                    <span className="font-medium">Blocked Initiatives:</span> {blockedInitiatives}
                </div>
            )}
        </div>
    );
};

export default IssueCard;
